import React, { useEffect, useState, useMemo, useRef } from 'react';
import { form4Api } from '../services/api';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, PointElement, LineElement,
  Title, Tooltip, Legend,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Bar, Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, zoomPlugin);

interface Form4Transaction {
  id: number;
  form4id: string;
  name: string | null;
  ticker: string | null;
  ownerName: string | null;
  director: number | null;
  officer: number | null;
  tenPercentOwner: number | null;
  other: number | null;
  title: string | null;
  otherText: string | null;
  securityTitle: string | null;
  date: string | null;
  code: string | null;
  shares: number | null;
  price: number | null;
  amount: number | null;
  totalShares: number | null;
}

interface DateInfo {
  date: string;
  total: number;
  buys: number;
  sells: number;
}

interface DailyData {
  date: string;
  buyCount: number;
  sellCount: number;
  buyAmount: number;
  sellAmount: number;
}

interface TopTicker {
  ticker: string;
  count: number;
  amount: number;
}

interface DashboardData {
  daily: DailyData[];
  topBuys30: TopTicker[];
  topSells30: TopTicker[];
  topBuys90: TopTicker[];
  topSells90: TopTicker[];
  topBuysByCount30: TopTicker[];
  topSellsByCount30: TopTicker[];
  topBuysByCount90: TopTicker[];
  topSellsByCount90: TopTicker[];
}

type TabKey = 'dashboard' | 'latestBuy' | 'latestSell' | 'weeklyBuy' | 'weeklySell' | 'search' | 'download' | 'backfill' | 'maintenance';

const tabLabels: { key: TabKey; label: string }[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'latestBuy', label: 'Latest Buys' },
  { key: 'latestSell', label: 'Latest Sells' },
  { key: 'weeklyBuy', label: 'Weekly Buys' },
  { key: 'weeklySell', label: 'Weekly Sells' },
  { key: 'search', label: 'Search' },
  { key: 'download', label: 'Download' },
  { key: 'backfill', label: 'Backfill' },
  { key: 'maintenance', label: 'Maintenance' },
];

function getPosition(t: Form4Transaction): string {
  const parts: string[] = [];
  if (t.director === 1) parts.push('Director');
  if (t.officer === 1) {
    if (t.title && t.title.length > 1) {
      // title may contain semicolons (merged) or commas — split and add each
      for (const p of t.title.split(/[;,]/).map(s => s.trim()).filter(Boolean)) {
        parts.push(p);
      }
    } else {
      parts.push('Officer');
    }
  }
  if (t.tenPercentOwner === 1) parts.push('10% Owner');
  if (t.other === 1) {
    parts.push('Other');
    if (t.otherText && t.otherText.length > 1 && !t.otherText.includes('refer')) {
      for (const p of t.otherText.split(/[;,]/).map(s => s.trim()).filter(Boolean)) {
        parts.push(p);
      }
    }
  }
  if (parts.length === 0) return 'Not set';
  // Deduplicate (case-insensitive)
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const p of parts) {
    const key = p.toLowerCase();
    if (!seen.has(key)) { seen.add(key); unique.push(p); }
  }
  return unique.join(', ');
}

/** Flip "LastName FirstName Middle" → "FirstName Middle LastName" and title-case. */
const flipName = (name: string): string => {
  if (!name) return name;
  // Handle multi-owner names separated by '; '
  if (name.includes('; ')) return name.split('; ').map(flipName).join('; ');
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name;
  const flipped = [...parts.slice(1), parts[0]].map(p =>
    p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()
  ).join(' ');
  return flipped;
};

const generateTweet = (t: Form4Transaction): string => {
  const name = (t.name || '')
    .replace(/\s*\/\w+\/\s*/g, ' ')   // remove state like /DE/, /NV/
    .replace(/,?\s*Inc\.?/g, '')
    .replace(/,?\s*Corp\.?/g, '')
    .replace(/,?\s*Ltd\.?/g, '')
    .replace(/,?\s*L\.?L\.?C\.?/g, '')
    .replace(/,?\s*L\.?P\.?/g, '')
    .replace(/,?\s*plc\.?/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  const owner = t.ownerName || '';
  const position = getPosition(t);
  const action = t.code === 'P' ? 'bought' : 'sold';
  const emoji = t.code === 'P' ? '🟢' : '🔴';
  const shares = (t.shares || 0).toLocaleString();
  const price = (t.price || 0).toFixed(2);
  const amt = t.amount || 0;
  const amountStr = amt >= 1_000_000
    ? `$${(amt / 1_000_000).toFixed(2)}M`
    : `$${amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const total = (t.totalShares || 0).toLocaleString();

  const lines = [
    `${emoji} SEC Form 4: #InsiderTrading`,
    ``,
    `On ${t.date}, ${owner}, ${position} of ${name} $${t.ticker}, ${action} ${shares} shares at $${price}/share (~${amountStr}).`,
    ``,
    `Now holds ${total} shares.`,
    ``,
    `#${t.ticker} #Insider${t.code === 'P' ? 'Buying' : 'Selling'} #SEC #Form4`,
  ];
  return lines.join('\n');
};

const copyToClipboard = (text: string) => {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  } else {
    fallbackCopy(text);
  }
};

const fallbackCopy = (text: string) => {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
};

/* ---- Helpers ---- */
const fmtAmt = (n: number) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : `$${n.toLocaleString()}`;
const yahooUrl = (ticker: string) => `https://finance.yahoo.com/quote/${encodeURIComponent(ticker)}`;
const TickerLink: React.FC<{ ticker: string | null; style?: React.CSSProperties }> = ({ ticker, style }) =>
  ticker ? (
    <a href={yahooUrl(ticker)} target="_blank" rel="noopener noreferrer"
      style={{ color: '#0069cc', fontWeight: 'bold', textDecoration: 'none', ...style }}>{ticker}</a>
  ) : <span>—</span>;

const TopTable: React.FC<{ title: string; items: TopTicker[]; color: string; onTickerClick?: (ticker: string) => void }> = ({ title, items, color, onTickerClick }) => (
  <div>
    <div style={{ fontWeight: 'bold', fontSize: '0.9em', marginBottom: '0.3em', color }}>{title}</div>
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={styles.th}>#</th>
          <th style={styles.th}>Ticker</th>
          <th style={styles.th}>Transactions</th>
          <th style={styles.th}>Total Amount</th>
        </tr>
      </thead>
      <tbody>
        {items.map((t, i) => (
          <tr key={t.ticker} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#e6f2ff' }}>
            <td style={{ ...styles.td, textAlign: 'center' }}>{i + 1}</td>
            <td style={styles.td}>
              {onTickerClick ? (
                <span style={{ ...styles.actionLink, fontWeight: 'bold' }} onClick={() => onTickerClick(t.ticker)}>{t.ticker}</span>
              ) : <TickerLink ticker={t.ticker} />}
            </td>
            <td style={{ ...styles.td, textAlign: 'right' }}>{t.count}</td>
            <td style={{ ...styles.td, textAlign: 'right' }}>{fmtAmt(t.amount)}</td>
          </tr>
        ))}
        {items.length === 0 && <tr><td colSpan={4} style={{ ...styles.td, color: '#999' }}>No data</td></tr>}
      </tbody>
    </table>
  </div>
);

/* ---- Dashboard Tab ---- */
const chartOpts = (title: string, totalLabels: number, yLabel?: string): any => {
  const defaultWindow = 60; // ~90 calendar days = ~60 business days
  const minIdx = Math.max(0, totalLabels - defaultWindow);
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top' as const, labels: { font: { size: 11 } } },
      title: { display: true, text: title, font: { size: 13 } },
      zoom: {
        pan: { enabled: true, mode: 'x' as const },
        zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' as const },
      },
    },
    scales: {
      x: {
        min: minIdx,
        ticks: { font: { size: 9 }, maxRotation: 45 },
      },
      y: { beginAtZero: true, title: yLabel ? { display: true, text: yLabel, font: { size: 11 } } : undefined },
    },
  };
};

/* ---- Dashboard tweet generators ---- */
const QUAL_NOTE = 'Note: Qualified transactions only (Buy >= $50K, Sell >= $500K).';

const tweetBuySellCount = (daily: DailyData[], type: 'buy' | 'sell') => {
  const recent = daily.slice(-5);
  const total = recent.reduce((s, d) => s + (type === 'buy' ? d.buyCount : d.sellCount), 0);
  const avg = Math.round(total / recent.length);
  const dateRange = `${daily[0]?.date} to ${daily[daily.length - 1]?.date}`;
  const emoji = type === 'buy' ? '\uD83D\uDFE2' : '\uD83D\uDD34';
  const label = type === 'buy' ? 'Buy' : 'Sell';
  return [
    `${emoji} SEC Form 4: Daily Insider ${label} Transactions (${dateRange})`,
    ``,
    `Last 5 days avg: ${avg} ${label.toLowerCase()} transactions/day`,
    `Recent: ${recent.map(d => `${d.date.slice(5)}: ${type === 'buy' ? d.buyCount : d.sellCount}`).join(', ')}`,
    ``,
    `${QUAL_NOTE}`,
    ``,
    `#InsiderTrading #SEC #Form4 #Insider${label === 'Buy' ? 'Buying' : 'Selling'}`,
  ].join('\n');
};

const tweetBuySellAmount = (daily: DailyData[], type: 'buy' | 'sell') => {
  const recent = daily.slice(-5);
  const total = recent.reduce((s, d) => s + (type === 'buy' ? d.buyAmount : d.sellAmount), 0);
  const dateRange = `${daily[0]?.date} to ${daily[daily.length - 1]?.date}`;
  const emoji = type === 'buy' ? '\uD83D\uDFE2' : '\uD83D\uDD34';
  const label = type === 'buy' ? 'Buy' : 'Sell';
  return [
    `${emoji} SEC Form 4: Daily Insider ${label} Amount (${dateRange})`,
    ``,
    `Last 5 days total: ${fmtAmt(total)}`,
    `Recent: ${recent.map(d => `${d.date.slice(5)}: ${fmtAmt(type === 'buy' ? d.buyAmount : d.sellAmount)}`).join(', ')}`,
    ``,
    `${QUAL_NOTE}`,
    ``,
    `#InsiderTrading #SEC #Form4 #Insider${label === 'Buy' ? 'Buying' : 'Selling'}`,
  ].join('\n');
};

const tweetRatio = (daily: DailyData[]) => {
  const recent = daily.slice(-5);
  const totalBuy = recent.reduce((s, d) => s + d.buyCount, 0);
  const totalSell = recent.reduce((s, d) => s + d.sellCount, 0);
  const totalBuyAmt = recent.reduce((s, d) => s + d.buyAmount, 0);
  const totalSellAmt = recent.reduce((s, d) => s + d.sellAmount, 0);
  const countRatio = totalSell > 0 ? (totalBuy / totalSell).toFixed(2) : 'N/A';
  const amtRatio = totalSellAmt > 0 ? (totalBuyAmt / totalSellAmt).toFixed(2) : 'N/A';
  const dateRange = `${daily[0]?.date} to ${daily[daily.length - 1]?.date}`;
  return [
    `\uD83D\uDCC8 SEC Form 4: Insider Buy/Sell Ratio (${dateRange})`,
    ``,
    `Last 5 days:`,
    `Buy/Sell ratio (count): ${countRatio} (${totalBuy} buys vs ${totalSell} sells)`,
    `Buy/Sell ratio (amount): ${amtRatio} (${fmtAmt(totalBuyAmt)} vs ${fmtAmt(totalSellAmt)})`,
    ``,
    `${QUAL_NOTE}`,
    ``,
    `#InsiderTrading #SEC #Form4 #BuySellRatio`,
  ].join('\n');
};

const tweetTopTable = (title: string, items: TopTicker[], type: 'buy' | 'sell') => {
  const emoji = type === 'buy' ? '\uD83D\uDFE2' : '\uD83D\uDD34';
  const tickers = items.slice(0, 10).map((t, i) => `${i + 1}. $${t.ticker} — ${fmtAmt(t.amount)} (${t.count} ${t.count === 1 ? 'txn' : 'txns'})`);
  const hashtags = items.slice(0, 5).map(t => `$${t.ticker}`).join(' ');
  return [
    `${emoji} ${title}`,
    ``,
    ...tickers,
    ``,
    `${hashtags}`,
    `${QUAL_NOTE}`,
    ``,
    `#InsiderTrading #SEC #Form4 #Insider${type === 'buy' ? 'Buying' : 'Selling'}`,
  ].join('\n');
};

const tweetTransactionList = (items: Form4Transaction[], tabLabel: string) => {
  if (!items.length) return '';
  const type = items[0]?.code === 'P' ? 'buy' : 'sell';
  const emoji = type === 'buy' ? '\uD83D\uDFE2' : '\uD83D\uDD34';
  const lines = items.slice(0, 20).map((t) => {
    const amt = t.amount || 0;
    const amtStr = amt >= 1_000_000 ? `$${(amt / 1_000_000).toFixed(1)}M` : `$${Math.round(amt).toLocaleString()}`;
    return `$${t.ticker} — ${t.ownerName?.split(';')[0] || ''} ${type === 'buy' ? 'bought' : 'sold'} ${amtStr}`;
  });
  const tickers = Array.from(new Set(items.slice(0, 10).map(t => `$${t.ticker}`))).join(' ');
  return [
    `${emoji} ${tabLabel}`,
    ``,
    ...lines,
    ``,
    `${tickers}`,
    `${QUAL_NOTE}`,
    ``,
    `#InsiderTrading #SEC #Form4 #Insider${type === 'buy' ? 'Buying' : 'Selling'}`,
  ].join('\n');
};

const PostButton: React.FC<{ getText: () => string }> = ({ getText }) => {
  const [status, setStatus] = useState<'idle' | 'copied' | 'posting' | 'posted' | 'error'>('idle');
  const handleCopy = () => { copyToClipboard(getText()); setStatus('copied'); setTimeout(() => setStatus('idle'), 2000); };
  const handlePost = () => {
    const text = getText();
    if (text.length > 25000) { alert(`Tweet too long (${text.length}/25000 chars). Please shorten it.`); return; }
    if (!window.confirm(`Post to X?\n\n${text}`)) return;
    setStatus('posting');
    form4Api.postToX(text, false)
      .then(() => { setStatus('posted'); setTimeout(() => setStatus('idle'), 5000); })
      .catch((err) => { setStatus('error'); alert(`Post failed: ${err?.response?.data?.detail || err.message}`); setTimeout(() => setStatus('idle'), 3000); });
  };
  const postColor = status === 'posted' ? 'green' : status === 'error' ? 'red' : '#e04530';
  const postLabel = status === 'posting' ? 'Posting...' : status === 'posted' ? 'Posted!' : status === 'error' ? 'Failed' : 'Post to X';
  return (
    <span style={{ marginLeft: '8px', fontSize: '0.75em' }}>
      <span
        style={{ cursor: status === 'posting' ? 'wait' : 'pointer', color: postColor, fontWeight: 'bold', marginRight: 6 }}
        onClick={status === 'posting' ? undefined : handlePost}
      >{postLabel}</span>
      <span
        style={{ cursor: 'pointer', color: status === 'copied' ? 'green' : '#888', fontWeight: 'normal' }}
        onClick={handleCopy}
      >{status === 'copied' ? 'Copied!' : 'Copy'}</span>
    </span>
  );
};

interface DailyPick {
  id: number;
  pick_date: string;
  rank_num: number;
  ticker: string;
  total_score: number;
  db_score: number | null;
  scores: Record<string, number> | null;
  price_data: Record<string, number> | null;
}

interface PickDateInfo { date: string; count: number; }

const Form4TopPicksSection: React.FC<{ onSearchTicker?: (ticker: string) => void }> = ({ onSearchTicker }) => {
  const [picks, setPicks] = useState<DailyPick[]>([]);
  const [dates, setDates] = useState<PickDateInfo[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [prevTickers, setPrevTickers] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState('');

  const loadPrevTickers = (currentDate: string, datesList: PickDateInfo[]) => {
    const idx = datesList.findIndex(d => d.date === currentDate);
    if (idx >= 0 && idx < datesList.length - 1) {
      const prevDate = datesList[idx + 1].date;
      form4Api.dailyPicks(prevDate)
        .then(res => setPrevTickers(new Set(res.data.map((p: DailyPick) => p.ticker))))
        .catch(() => setPrevTickers(new Set()));
    } else {
      setPrevTickers(new Set());
    }
  };

  useEffect(() => {
    Promise.all([form4Api.dailyPicks(), form4Api.dailyPicksDates()])
      .then(([picksRes, datesRes]) => {
        setPicks(picksRes.data);
        setDates(datesRes.data);
        if (picksRes.data.length > 0) {
          setSelectedDate(picksRes.data[0].pick_date);
          loadPrevTickers(picksRes.data[0].pick_date, datesRes.data);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleDateChange = (d: string) => {
    setSelectedDate(d);
    setLoading(true);
    form4Api.dailyPicks(d)
      .then((res) => { setPicks(res.data); loadPrevTickers(d, dates); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleGenerate = () => {
    if (!window.confirm('Generate today\'s picks? This may take a few minutes.')) return;
    setGenerating(true);
    setMessage('');
    form4Api.dailyPicksGenerate()
      .then((res) => {
        const d = res.data;
        setMessage(`Generated ${d.count} picks for ${d.date}`);
        return Promise.all([form4Api.dailyPicks(), form4Api.dailyPicksDates()]);
      })
      .then(([picksRes, datesRes]) => {
        setPicks(picksRes.data);
        setDates(datesRes.data);
        if (picksRes.data.length > 0) setSelectedDate(picksRes.data[0].pick_date);
      })
      .catch((err: any) => setMessage(`Error: ${err?.response?.data?.detail || err.message}`))
      .finally(() => setGenerating(false));
  };

  const scoreLabel = (key: string) => {
    const map: Record<string, string> = {
      continuity: 'Cont', position: 'Pos', buy_price_flat: 'BPF', price_36m: 'P36M',
      trend: 'Trend', buy_count: 'BuyCt', bottom: 'Bttm',
      abs_price: 'AbsP', mcap_bonus: 'MCap', top_buyer_penalty: 'TopBP',
      vtrap_penalty: 'VTrap', sector_penalty: 'Sect',
      insider_bonus: 'InsBon', sell_penalty: 'SellP',
    };
    return map[key] || key;
  };

  const scoreKeys = ['buy_price_flat', 'price_36m', 'trend', 'abs_price', 'mcap_bonus', 'top_buyer_penalty', 'vtrap_penalty', 'sector_penalty', 'insider_bonus', 'sell_penalty'];

  return (
    <div style={{ marginBottom: '1.5em' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1em', marginBottom: '0.5em' }}>
        <span style={{ fontWeight: 'bold', fontSize: '0.95em', color: '#0069cc' }}>Top Picks</span>
        {dates.length > 0 && (
          <select value={selectedDate} onChange={(e) => handleDateChange(e.target.value)}
            style={{ padding: '2px 4px', fontSize: '0.85em' }}>
            {dates.map((d) => (
              <option key={d.date} value={d.date}>{d.date} ({d.count} picks)</option>
            ))}
          </select>
        )}
        <button onClick={handleGenerate} disabled={generating}
          style={{ ...styles.button, fontSize: '0.8em', padding: '2px 10px' }}>
          {generating ? 'Generating...' : 'Generate Now'}
        </button>
        {message && <span style={{ fontSize: '0.8em', color: message.startsWith('Error') ? 'red' : 'green' }}>{message}</span>}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '1em', fontSize: '0.85em' }}>Loading picks...</div>
      ) : picks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '1em', fontSize: '0.85em', color: '#999' }}>
          No picks available. Click "Generate Now" to create today's picks.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Ticker</th>
                <th style={styles.th}>Score</th>
                <th style={styles.th}>Price</th>
                {scoreKeys.map((k) => (
                  <th key={k} style={{ ...styles.th, fontSize: '0.7em' }}>{scoreLabel(k)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {picks.map((p, i) => {
                const s = p.scores || {};
                const pd = p.price_data || {};
                const isNew = prevTickers.size > 0 && !prevTickers.has(p.ticker);
                return (
                  <tr key={p.id} style={{ backgroundColor: isNew ? '#ffffcc' : (i % 2 === 0 ? '#fff' : '#e6f2ff') }}>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{p.rank_num}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.actionLink, fontWeight: 'bold' }}
                        onClick={() => onSearchTicker?.(p.ticker)}>{p.ticker}</span>
                      {isNew && <span style={{ marginLeft: 4, fontSize: '0.65em', color: '#e65100', fontWeight: 'bold' }}>NEW</span>}
                      <a href={`https://finance.yahoo.com/quote/${p.ticker}/`} target="_blank" rel="noreferrer"
                        style={{ marginLeft: 4, fontSize: '0.7em', color: '#888' }}>YF</a>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 'bold' }}>{p.total_score.toFixed(2)}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{pd.current_price != null ? `$${pd.current_price.toFixed(2)}` : ''}</td>
                    {scoreKeys.map((k) => {
                      const v = s[k];
                      const isBonus = ['mcap_bonus', 'top_buyer_penalty', 'vtrap_penalty', 'sector_penalty', 'insider_bonus', 'sell_penalty'].includes(k);
                      const fmt = v != null && typeof v === 'number'
                        ? (isBonus ? (v >= 0 ? '+' : '') + v.toFixed(1) : v.toFixed(1))
                        : (v ?? '');
                      const color = isBonus && v != null ? (v > 0 ? '#2e7d32' : v < 0 ? '#c62828' : undefined) : undefined;
                      return (
                        <td key={k} style={{ ...styles.td, textAlign: 'right', fontSize: '0.75em', color }}>
                          {fmt}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const DashboardTab: React.FC<{ onSearchTicker?: (ticker: string) => void }> = ({ onSearchTicker }) => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    form4Api.dashboard()
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const labels = useMemo(() => data?.daily.map(d => d.date.slice(5)) || [], [data]);

  if (loading) return <div style={{ textAlign: 'center', padding: '2em' }}>Loading dashboard...</div>;
  if (!data || data.daily.length === 0) return <div style={{ textAlign: 'center', padding: '2em', color: '#999' }}>No data available for dashboard.</div>;

  const buyCountData = {
    labels,
    datasets: [{
      label: 'Buy Transactions',
      data: data.daily.map(d => d.buyCount),
      backgroundColor: 'rgba(76, 175, 80, 0.7)',
    }],
  };

  const sellCountData = {
    labels,
    datasets: [{
      label: 'Sell Transactions',
      data: data.daily.map(d => d.sellCount),
      backgroundColor: 'rgba(244, 67, 54, 0.7)',
    }],
  };

  const buyAmountData = {
    labels,
    datasets: [{
      label: 'Buy Amount ($)',
      data: data.daily.map(d => d.buyAmount),
      backgroundColor: 'rgba(76, 175, 80, 0.7)',
    }],
  };

  const sellAmountData = {
    labels,
    datasets: [{
      label: 'Sell Amount ($)',
      data: data.daily.map(d => d.sellAmount),
      backgroundColor: 'rgba(244, 67, 54, 0.7)',
    }],
  };

  const ratioData = {
    labels,
    datasets: [
      {
        label: 'Buy/Sell Ratio (Count)',
        data: data.daily.map(d => d.sellCount > 0 ? +(d.buyCount / d.sellCount).toFixed(2) : 0),
        borderColor: '#2196f3',
        backgroundColor: 'rgba(33, 150, 243, 0.1)',
        tension: 0.3,
        fill: false,
        yAxisID: 'yCount',
      },
      {
        label: 'Buy/Sell Ratio (Amount)',
        data: data.daily.map(d => d.sellAmount > 0 ? +(d.buyAmount / d.sellAmount).toFixed(2) : 0),
        borderColor: '#ff9800',
        backgroundColor: 'rgba(255, 152, 0, 0.1)',
        tension: 0.3,
        fill: false,
        yAxisID: 'yAmount',
      },
    ],
  };

  const defaultWindow = 60;
  const ratioMinIdx = Math.max(0, labels.length - defaultWindow);
  const ratioOpts: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top' as const, labels: { font: { size: 11 } } },
      title: { display: true, text: 'Buy/Sell Ratio Over Time', font: { size: 13 } },
      zoom: {
        pan: { enabled: true, mode: 'x' as const },
        zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' as const },
      },
    },
    scales: {
      x: { min: ratioMinIdx, ticks: { font: { size: 9 }, maxRotation: 45 } },
      yCount: {
        type: 'linear' as const,
        position: 'left' as const,
        beginAtZero: true,
        title: { display: true, text: 'Ratio (Count)', font: { size: 11 }, color: '#2196f3' },
        ticks: { color: '#2196f3' },
      },
      yAmount: {
        type: 'linear' as const,
        position: 'right' as const,
        beginAtZero: true,
        title: { display: true, text: 'Ratio (Amount)', font: { size: 11 }, color: '#ff9800' },
        ticks: { color: '#ff9800' },
        grid: { drawOnChartArea: false },
      },
    },
  };

  return (
    <div style={{ marginTop: '0.5em' }}>
      <Form4TopPicksSection onSearchTicker={onSearchTicker} />
      {/* Charts grid: 2x2 bar charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em', marginBottom: '1em' }}>
        <div style={{ height: 220, padding: '8px', border: '1px solid #ddd', borderRadius: 4, position: 'relative' }}>
          <Bar data={buyCountData} options={chartOpts('Daily Buy Transactions (Count)', labels.length)} />
          <PostButton getText={() => tweetBuySellCount(data.daily, 'buy')} />
        </div>
        <div style={{ height: 220, padding: '8px', border: '1px solid #ddd', borderRadius: 4, position: 'relative' }}>
          <Bar data={sellCountData} options={chartOpts('Daily Sell Transactions (Count)', labels.length)} />
          <PostButton getText={() => tweetBuySellCount(data.daily, 'sell')} />
        </div>
        <div style={{ height: 220, padding: '8px', border: '1px solid #ddd', borderRadius: 4, position: 'relative' }}>
          <Bar data={buyAmountData} options={chartOpts('Daily Buy Amount ($)', labels.length)} />
          <PostButton getText={() => tweetBuySellAmount(data.daily, 'buy')} />
        </div>
        <div style={{ height: 220, padding: '8px', border: '1px solid #ddd', borderRadius: 4, position: 'relative' }}>
          <Bar data={sellAmountData} options={chartOpts('Daily Sell Amount ($)', labels.length)} />
          <PostButton getText={() => tweetBuySellAmount(data.daily, 'sell')} />
        </div>
      </div>

      {/* Buy/Sell ratio line chart */}
      <div style={{ height: 250, padding: '8px', border: '1px solid #ddd', borderRadius: 4, marginBottom: '1em', position: 'relative' }}>
        <Line data={ratioData} options={ratioOpts} />
        <PostButton getText={() => tweetRatio(data.daily)} />
      </div>

      {/* Top 10 tables: 30 days */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em', marginBottom: '1em' }}>
        <div>
          <TopTable title="Top 10 Insider Buys (Last 30 Days)" items={data.topBuys30} color="green" onTickerClick={onSearchTicker} />
          <PostButton getText={() => tweetTopTable('Top 10 Insider Buys (Last 30 Days)', data.topBuys30, 'buy')} />
        </div>
        <div>
          <TopTable title="Top 10 Insider Sells (Last 30 Days)" items={data.topSells30} color="red" onTickerClick={onSearchTicker} />
          <PostButton getText={() => tweetTopTable('Top 10 Insider Sells (Last 30 Days)', data.topSells30, 'sell')} />
        </div>
      </div>
      {/* Top 10 tables: 90 days */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em', marginBottom: '1em' }}>
        <div>
          <TopTable title="Top 10 Insider Buys (Last 90 Days)" items={data.topBuys90} color="green" onTickerClick={onSearchTicker} />
          <PostButton getText={() => tweetTopTable('Top 10 Insider Buys (Last 90 Days)', data.topBuys90, 'buy')} />
        </div>
        <div>
          <TopTable title="Top 10 Insider Sells (Last 90 Days)" items={data.topSells90} color="red" onTickerClick={onSearchTicker} />
          <PostButton getText={() => tweetTopTable('Top 10 Insider Sells (Last 90 Days)', data.topSells90, 'sell')} />
        </div>
      </div>

      {/* Top 10 by transaction count: 30 days */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em', marginBottom: '1em' }}>
        <div>
          <TopTable title="Top 10 Insider Buys by # of Transactions (30 Days)" items={data.topBuysByCount30} color="green" onTickerClick={onSearchTicker} />
          <PostButton getText={() => tweetTopTable('Top 10 Insider Buys by # of Transactions (30 Days)', data.topBuysByCount30, 'buy')} />
        </div>
        <div>
          <TopTable title="Top 10 Insider Sells by # of Transactions (30 Days)" items={data.topSellsByCount30} color="red" onTickerClick={onSearchTicker} />
          <PostButton getText={() => tweetTopTable('Top 10 Insider Sells by # of Transactions (30 Days)', data.topSellsByCount30, 'sell')} />
        </div>
      </div>
      {/* Top 10 by transaction count: 90 days */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em' }}>
        <div>
          <TopTable title="Top 10 Insider Buys by # of Transactions (90 Days)" items={data.topBuysByCount90} color="green" onTickerClick={onSearchTicker} />
          <PostButton getText={() => tweetTopTable('Top 10 Insider Buys by # of Transactions (90 Days)', data.topBuysByCount90, 'buy')} />
        </div>
        <div>
          <TopTable title="Top 10 Insider Sells by # of Transactions (90 Days)" items={data.topSellsByCount90} color="red" onTickerClick={onSearchTicker} />
          <PostButton getText={() => tweetTopTable('Top 10 Insider Sells by # of Transactions (90 Days)', data.topSellsByCount90, 'sell')} />
        </div>
      </div>
    </div>
  );
};

/* ---- Maintenance Tab (Outlier Detection) ---- */
interface OutlierTransaction extends Form4Transaction {
  outlier_reason?: string;
}

type OutlierSortKey = 'reported' | 'ticker' | 'owner' | 'date' | 'code' | 'shares' | 'price' | 'amount' | 'reason';

const MaintenanceTab: React.FC<{ onSearchTicker?: (ticker: string) => void }> = ({ onSearchTicker }) => {
  const [outliers, setOutliers] = useState<OutlierTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sortKey, setSortKey] = useState<OutlierSortKey>('amount');
  const [sortAsc, setSortAsc] = useState(false);
  const [delStart, setDelStart] = useState('');
  const [delEnd, setDelEnd] = useState('');
  const [deleting, setDeleting] = useState(false);

  const loadOutliers = () => {
    setLoading(true);
    setMessage('');
    form4Api.outliers()
      .then((res) => setOutliers(res.data))
      .catch((err) => setMessage(`Error: ${err.message}`))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadOutliers(); }, []);

  const handleSort = (key: OutlierSortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(key === 'ticker' || key === 'owner' || key === 'reported' || key === 'date');
    }
  };

  const sorted = useMemo(() => {
    const arr = [...outliers];
    const dir = sortAsc ? 1 : -1;
    arr.sort((a, b) => {
      let av: any, bv: any;
      switch (sortKey) {
        case 'reported': av = a.form4id; bv = b.form4id; break;
        case 'ticker': av = a.ticker || ''; bv = b.ticker || ''; break;
        case 'owner': av = a.ownerName || ''; bv = b.ownerName || ''; break;
        case 'date': av = a.date || ''; bv = b.date || ''; break;
        case 'code': av = a.code || ''; bv = b.code || ''; break;
        case 'shares': av = a.shares || 0; bv = b.shares || 0; break;
        case 'price': av = a.price || 0; bv = b.price || 0; break;
        case 'amount': av = a.amount || 0; bv = b.amount || 0; break;
        case 'reason': av = a.outlier_reason || ''; bv = b.outlier_reason || ''; break;
      }
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
    return arr;
  }, [outliers, sortKey, sortAsc]);

  const handleRemove = async (t: OutlierTransaction) => {
    if (!window.confirm(`Remove outlier transaction ${t.form4id} for ${t.ticker}?\nAmount: $${(t.amount || 0).toLocaleString()}\nReason: ${t.outlier_reason}`)) return;
    try {
      await form4Api.delete(t.id);
      setOutliers(prev => prev.filter(o => o.id !== t.id));
      setMessage(`Removed transaction ${t.form4id} (${t.ticker}).`);
    } catch (err: any) {
      setMessage(`Error removing: ${err.message}`);
    }
  };

  const handleQuickRemove = async (t: OutlierTransaction) => {
    try {
      await form4Api.delete(t.id);
      setOutliers(prev => prev.filter(o => o.id !== t.id));
    } catch (err: any) {
      setMessage(`Error removing: ${err.message}`);
    }
  };

  const handleRemoveAll = async () => {
    if (!window.confirm(`Remove ALL ${outliers.length} outlier transactions? This cannot be undone.`)) return;
    let removed = 0;
    for (const t of outliers) {
      try {
        await form4Api.delete(t.id);
        removed++;
      } catch { /* skip */ }
    }
    setMessage(`Removed ${removed} of ${outliers.length} outlier transactions.`);
    loadOutliers();
  };

  const sortArrow = (key: OutlierSortKey) => sortKey === key ? (sortAsc ? ' \u25B2' : ' \u25BC') : '';
  const thSort = (key: OutlierSortKey, label: string): React.CSSProperties & { onClick?: any } => ({
    ...styles.th, cursor: 'pointer', userSelect: 'none' as const,
  });

  return (
    <div style={{ marginTop: '0.5em' }}>
      <div style={{ padding: '10px 14px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', fontSize: '0.85em', marginBottom: '1em' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '1em' }}>Outlier Detection</div>
        <p style={{ margin: '0 0 8px 0', color: '#555' }}>
          Finds transactions with unusual amounts that may be caused by data entry errors.
          Criteria: statistical outliers (Z-score &gt; 3), amount mismatches (shares x price vs amount), and extremely large amounts (&gt; $500M).
        </p>
        <div style={{ display: 'flex', gap: '0.5em' }}>
          <input type="button" value="Refresh" onClick={loadOutliers} disabled={loading}
            style={{ ...styles.button, backgroundColor: '#b3d9ff', fontWeight: 'bold' }} />
          {outliers.length > 0 && (
            <input type="button" value={`Remove All (${outliers.length})`} onClick={handleRemoveAll}
              style={{ ...styles.button, backgroundColor: '#ffcccc', fontWeight: 'bold' }} />
          )}
        </div>
      </div>

      {/* Delete by date range */}
      <div style={{ padding: '10px 14px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', fontSize: '0.85em', marginBottom: '1em' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '1em' }}>Delete Data by Date Range</div>
        <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center', flexWrap: 'wrap' }}>
          <label>From:</label>
          <input type="date" value={delStart} onChange={(e) => setDelStart(e.target.value)}
            style={{ ...styles.input, width: '11em' }} />
          <label>To:</label>
          <input type="date" value={delEnd} onChange={(e) => setDelEnd(e.target.value)}
            style={{ ...styles.input, width: '11em' }} />
          <input type="button"
            value={deleting ? 'Deleting...' : 'Delete Range'}
            disabled={deleting || !delStart || !delEnd}
            onClick={async () => {
              if (!window.confirm(`Delete ALL Form4 data from ${delStart} to ${delEnd}? This cannot be undone.`)) return;
              setDeleting(true);
              try {
                const res = await form4Api.deleteByDateRange(delStart, delEnd);
                setMessage(`Deleted ${res.data.deleted} transactions from ${delStart} to ${delEnd}.`);
              } catch (err: any) {
                setMessage(`Error: ${err.response?.data?.detail || err.message}`);
              } finally {
                setDeleting(false);
              }
            }}
            style={{ ...styles.button, backgroundColor: '#ffcccc', fontWeight: 'bold' }} />
        </div>
      </div>

      {message && (
        <div style={{
          fontSize: '0.85em', marginBottom: '0.5em', padding: '6px 12px',
          backgroundColor: message.startsWith('Error') ? '#f8d7da' : '#d4edda',
          borderRadius: '4px', fontWeight: 'bold',
        }}>
          {message}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2em' }}>Scanning for outliers...</div>
      ) : outliers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2em', color: '#999' }}>No outliers detected. Data looks clean!</div>
      ) : (
        <>
          <div style={{ fontSize: '0.85em', marginBottom: '0.5em', color: '#555' }}>
            Found <strong>{outliers.length}</strong> potential outlier transactions. Click column headers to sort.
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={thSort('reported', 'Reported')} onClick={() => handleSort('reported')}>Reported{sortArrow('reported')}</th>
                <th style={styles.th}>Company</th>
                <th style={thSort('ticker', 'Ticker')} onClick={() => handleSort('ticker')}>Ticker{sortArrow('ticker')}</th>
                <th style={thSort('owner', 'Owner')} onClick={() => handleSort('owner')}>Owner{sortArrow('owner')}</th>
                <th style={thSort('date', 'Date')} onClick={() => handleSort('date')}>Date{sortArrow('date')}</th>
                <th style={thSort('code', 'Type')} onClick={() => handleSort('code')}>Type{sortArrow('code')}</th>
                <th style={thSort('shares', 'Shares')} onClick={() => handleSort('shares')}>Shares{sortArrow('shares')}</th>
                <th style={thSort('price', 'Price')} onClick={() => handleSort('price')}>Price{sortArrow('price')}</th>
                <th style={thSort('amount', 'Amount')} onClick={() => handleSort('amount')}>Amount{sortArrow('amount')}</th>
                <th style={thSort('reason', 'Outlier Reason')} onClick={() => handleSort('reason')}>Outlier Reason{sortArrow('reason')}</th>
                <th style={styles.th}>Remove</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((t, idx) => {
                const reported = t.form4id.replace(/(\d{4})(\d{2})(\d{2})-(\d{3})/, '$1-$2-$3');
                return (
                  <tr key={t.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                    <td style={styles.td}>{reported}</td>
                    <td style={{ ...styles.td, fontSize: '0.75em', color: '#555' }}>{t.name}</td>
                    <td style={styles.td}>
                      {onSearchTicker ? (
                        <span style={{ ...styles.actionLink, fontWeight: 'bold' }} onClick={() => onSearchTicker(t.ticker || '')}>{t.ticker || '—'}</span>
                      ) : <TickerLink ticker={t.ticker} />}
                    </td>
                    <td style={styles.td}>{t.ownerName}</td>
                    <td style={styles.td}>{t.date}</td>
                    <td style={{ ...styles.td, textAlign: 'center', color: t.code === 'P' ? 'green' : 'red', fontWeight: 'bold' }}>
                      {t.code === 'P' ? 'Buy' : t.code === 'S' ? 'Sell' : t.code}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{t.shares?.toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{t.price?.toFixed(2)}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 'bold', color: '#c00' }}>
                      {t.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ ...styles.td, fontSize: '0.75em', color: '#666' }}>{t.outlier_reason}</td>
                    <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <span style={{ ...styles.actionLink, color: '#c00' }} onClick={() => handleQuickRemove(t)}>X</span>
                      <span style={{ margin: '0 4px', color: '#ccc' }}>|</span>
                      <span style={{ ...styles.actionLink, color: '#888' }} onClick={() => handleRemove(t)}>?</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

/* ---- Daily Summary Table (last 90 days) ---- */
type DailySortKey = 'date' | 'buyCount' | 'buyAmount' | 'sellCount' | 'sellAmount' | 'totalCount' | 'net';

const DailySummaryTable: React.FC = () => {
  const [daily, setDaily] = useState<DailyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<DailySortKey>('date');
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => {
    form4Api.dashboard()
      .then((res) => setDaily(res.data.daily))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSort = (key: DailySortKey) => {
    if (sortKey === key) { setSortAsc(!sortAsc); }
    else { setSortKey(key); setSortAsc(key === 'date'); }
  };

  const sorted = useMemo(() => {
    const arr = [...daily];
    const dir = sortAsc ? 1 : -1;
    arr.sort((a, b) => {
      let av: number, bv: number;
      switch (sortKey) {
        case 'date': av = Date.parse(a.date); bv = Date.parse(b.date); break;
        case 'buyCount': av = a.buyCount; bv = b.buyCount; break;
        case 'buyAmount': av = a.buyAmount; bv = b.buyAmount; break;
        case 'sellCount': av = a.sellCount; bv = b.sellCount; break;
        case 'sellAmount': av = a.sellAmount; bv = b.sellAmount; break;
        case 'totalCount': av = a.buyCount + a.sellCount; bv = b.buyCount + b.sellCount; break;
        case 'net': av = a.buyAmount - a.sellAmount; bv = b.buyAmount - b.sellAmount; break;
      }
      return av < bv ? -dir : av > bv ? dir : 0;
    });
    return arr;
  }, [daily, sortKey, sortAsc]);

  if (loading) return <div style={{ textAlign: 'center', padding: '1em', fontSize: '0.85em' }}>Loading daily summary...</div>;
  if (daily.length === 0) return null;

  const arrow = (key: DailySortKey) => sortKey === key ? (sortAsc ? ' \u25B2' : ' \u25BC') : '';
  const thS: React.CSSProperties = { ...styles.th, cursor: 'pointer', userSelect: 'none' };

  return (
    <div style={{ marginBottom: '1em' }}>
      <div style={{ fontWeight: 'bold', fontSize: '0.9em', marginBottom: '0.3em' }}>Daily Summary (Last 90 Days) — click headers to sort</div>
      <div style={{ maxHeight: '360px', overflowY: 'auto', border: '1px solid #ddd' }}>
        <table style={{ ...styles.table, marginTop: 0 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <tr>
              <th style={thS} onClick={() => handleSort('date')}>Date{arrow('date')}</th>
              <th style={thS} onClick={() => handleSort('buyCount')}>Buy Count{arrow('buyCount')}</th>
              <th style={thS} onClick={() => handleSort('buyAmount')}>Buy Amount{arrow('buyAmount')}</th>
              <th style={thS} onClick={() => handleSort('sellCount')}>Sell Count{arrow('sellCount')}</th>
              <th style={thS} onClick={() => handleSort('sellAmount')}>Sell Amount{arrow('sellAmount')}</th>
              <th style={thS} onClick={() => handleSort('totalCount')}>Total Count{arrow('totalCount')}</th>
              <th style={thS} onClick={() => handleSort('net')}>Net Amount{arrow('net')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((d, idx) => {
              const net = d.buyAmount - d.sellAmount;
              return (
                <tr key={d.date} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                  <td style={styles.td}>{d.date}</td>
                  <td style={{ ...styles.td, textAlign: 'right', color: 'green' }}>{d.buyCount}</td>
                  <td style={{ ...styles.td, textAlign: 'right', color: 'green' }}>{fmtAmt(d.buyAmount)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', color: 'red' }}>{d.sellCount}</td>
                  <td style={{ ...styles.td, textAlign: 'right', color: 'red' }}>{fmtAmt(d.sellAmount)}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{d.buyCount + d.sellCount}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 'bold', color: net >= 0 ? 'green' : 'red' }}>
                    {net >= 0 ? '+' : ''}{fmtAmt(Math.abs(net))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Form4Page: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('dashboard');
  const [items, setItems] = useState<Form4Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTicker, setSearchTicker] = useState('');
  const [searchForm4id, setSearchForm4id] = useState('');
  const [searchAmount, setSearchAmount] = useState('');
  const [tweetId, setTweetId] = useState<number | null>(null);
  const [popularTickers, setPopularTickers] = useState<Set<string>>(new Set());

  useEffect(() => {
    form4Api.popularTickers().then((res) => setPopularTickers(new Set(res.data))).catch(() => {});
  }, []);

  const loadTab = (t: TabKey) => {
    if (t === 'search' || t === 'download' || t === 'backfill' || t === 'dashboard' || t === 'maintenance') { setLoading(false); return; }
    setLoading(true);
    const fn = { latestBuy: form4Api.latestBuy, latestSell: form4Api.latestSell, weeklyBuy: form4Api.weeklyBuy, weeklySell: form4Api.weeklySell }[t];
    fn().then((res) => setItems(res.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadTab(tab); }, [tab]);

  const handleSearch = () => {
    setLoading(true);
    const params: any = {};
    if (searchTicker) params.ticker = searchTicker;
    else if (searchForm4id) params.form4id = searchForm4id;
    else if (searchAmount) params.min_amount = parseFloat(searchAmount);
    form4Api.search(params).then((res) => setItems(res.data)).catch(() => {}).finally(() => setLoading(false));
  };

  const handleSearchTicker = (ticker: string) => {
    setSearchTicker(ticker);
    setItems([]);
    setTab('search');
    setLoading(true);
    form4Api.search({ ticker }).then((res) => setItems(res.data)).catch(() => {}).finally(() => setLoading(false));
  };

  return (
    <div style={styles.container}>
      <h4 style={styles.heading}>Form 4 Insider Transactions (SEC)</h4>
      <div style={styles.tabs}>
        {tabLabels.map((t) => (
          <span
            key={t.key}
            onClick={() => { setTab(t.key); if (t.key !== 'search' && t.key !== 'download' && t.key !== 'backfill' && t.key !== 'dashboard' && t.key !== 'maintenance') setItems([]); }}
            style={{ ...styles.tab, ...(tab === t.key ? styles.activeTab : {}) }}
          >
            {t.label}
          </span>
        ))}
      </div>

      {tab === 'dashboard' ? (
        <DashboardTab onSearchTicker={handleSearchTicker} />
      ) : tab === 'maintenance' ? (
        <MaintenanceTab onSearchTicker={handleSearchTicker} />
      ) : tab === 'backfill' ? (
        <BackfillTab />
      ) : tab === 'download' ? (
        <DownloadTab />
      ) : (
        <>
          {tab === 'search' && (
            <>
              <DailySummaryTable />
              <div style={styles.searchBar}>
                <label>Ticker: </label>
                <input value={searchTicker} onChange={(e) => setSearchTicker(e.target.value.toUpperCase())} style={styles.input}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
                <label style={{ marginLeft: '1em' }}>Form4 ID: </label>
                <input value={searchForm4id} onChange={(e) => setSearchForm4id(e.target.value)} style={styles.input}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
                <label style={{ marginLeft: '1em' }}>Min Amount: </label>
                <input value={searchAmount} onChange={(e) => setSearchAmount(e.target.value)} style={{ ...styles.input, width: '8em' }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
                <input type="button" value="Search" onClick={handleSearch} style={{ ...styles.button, marginLeft: '1em' }} />
              </div>
            </>
          )}

          {!loading && items.length > 0 && ['latestBuy', 'latestSell', 'weeklyBuy', 'weeklySell'].includes(tab) && (
            <div style={{ marginBottom: '0.5em' }}>
              <PostButton getText={() => tweetTransactionList(items, tabLabels.find(t => t.key === tab)?.label || tab)} />
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Reported</th>
                  <th style={styles.th}>Company</th>
                  <th style={styles.th}>Ticker</th>
                  <th style={styles.th}>Owner</th>
                  <th style={styles.th}>Position</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Shares</th>
                  <th style={styles.th}>Price</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Total Shares</th>
                  <th style={styles.th}>Tweet</th>
                  <th style={styles.th}>Remove</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t, idx) => {
                  const rowBg = idx % 2 === 0 ? '#fff' : '#e6f2ff';
                  const reported = t.form4id.replace(/(\d{4})(\d{2})(\d{2})-(\d{3})/, '$1-$2-$3');
                  return (
                    <React.Fragment key={t.id}>
                      <tr style={{ backgroundColor: rowBg }}>
                        <td style={styles.td}>{reported}</td>
                        <td style={{ ...styles.td, fontSize: '0.75em', color: '#555' }}>{t.name}</td>
                        <td style={{ ...styles.td, ...(popularTickers.has(t.ticker || '') ? { backgroundColor: '#ffffb3' } : {}) }}>
                          {tab === 'search' ? (
                            <TickerLink ticker={t.ticker} />
                          ) : (
                            <span style={{ ...styles.actionLink, fontWeight: 'bold' }}
                              onClick={() => handleSearchTicker(t.ticker || '')}>{t.ticker || '—'}</span>
                          )}
                        </td>
                        <td style={styles.td}>{t.ownerName}</td>
                        <td style={styles.td}>{getPosition(t)}</td>
                        <td style={styles.td}>{t.date}</td>
                        <td style={{ ...styles.td, textAlign: 'center', color: t.code === 'P' ? 'green' : 'red', fontWeight: 'bold' }}>
                          {t.code === 'P' ? 'Buy' : t.code === 'S' ? 'Sell' : t.code}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>{t.shares?.toLocaleString()}</td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>{t.price?.toFixed(2)}</td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>{t.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>{t.totalShares?.toLocaleString()}</td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <span style={styles.actionLink} onClick={() => setTweetId(tweetId === t.id ? null : t.id)}>Tweet</span>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <span style={{ ...styles.actionLink, color: '#c00' }} onClick={async () => {
                            if (!window.confirm(`Remove Form4 transaction ${t.form4id} for ${t.ticker}?`)) return;
                            await form4Api.delete(t.id);
                            setItems(prev => prev.filter(i => i.id !== t.id));
                          }}>Remove</span>
                        </td>
                      </tr>
                      {tweetId === t.id && (
                        <tr style={{ backgroundColor: '#fff9e6' }}>
                          <td colSpan={13} style={{ ...styles.td, padding: '8px 12px', fontSize: '0.85em', whiteSpace: 'pre-wrap' }}>
                            <strong>Tweet:</strong>{'\n'}{generateTweet(t)}
                            <PostButton getText={() => generateTweet(t)} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
          {!loading && items.length === 0 && tab !== 'search' && <p style={{ textAlign: 'center', color: '#999' }}>No transactions found</p>}
        </>
      )}
    </div>
  );
};

/* ---- Progress Bar ---- */
const ProgressBar: React.FC<{ current: number; total: number; label: string; color?: string }> = ({ current, total, label, color = '#4caf50' }) => {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div style={{ marginBottom: '0.5em' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8em', marginBottom: '2px' }}>
        <span>{label}</span>
        <span>{current.toLocaleString()} / {total.toLocaleString()} ({pct}%)</span>
      </div>
      <div style={{ height: '18px', backgroundColor: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${pct}%`, backgroundColor: color,
          borderRadius: '4px', transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );
};

/* ---- Download Tab ---- */
const DownloadTab: React.FC = () => {
  const [reportDate, setReportDate] = useState(() => {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  });
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState('');
  const [dates, setDates] = useState<DateInfo[]>([]);
  const [loadingDates, setLoadingDates] = useState(true);
  // Progress state
  const [phase, setPhase] = useState<'idle' | 'downloading' | 'parsing' | 'done'>('idle');
  const [dlCurrent, setDlCurrent] = useState(0);
  const [dlTotal, setDlTotal] = useState(0);
  const [dlDownloaded, setDlDownloaded] = useState(0);
  const [parseCurrent, setParseCurrent] = useState(0);
  const [parseTotal, setParseTotal] = useState(0);
  const [qualified, setQualified] = useState(0);

  const loadDates = () => {
    setLoadingDates(true);
    form4Api.dates()
      .then((res) => setDates(res.data))
      .catch(() => {})
      .finally(() => setLoadingDates(false));
  };

  useEffect(() => { loadDates(); }, []);

  const handleDownload = () => {
    if (!reportDate) return;
    setDownloading(true);
    setResult('');
    setPhase('downloading');
    setDlCurrent(0);
    setDlTotal(0);
    setDlDownloaded(0);
    setParseCurrent(0);
    setParseTotal(0);
    setQualified(0);

    const token = localStorage.getItem('token');
    const url = `/api/form4/download?report_date=${reportDate}`;

    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.detail || `HTTP ${response.status}`);
        }
        const reader = response.body?.getReader();
        if (!reader) throw new Error('No reader');
        const decoder = new TextDecoder();
        let buffer = '';

        const read = (): Promise<void> =>
          reader.read().then(({ done, value }) => {
            if (done) {
              setDownloading(false);
              setPhase('done');
              loadDates();
              return;
            }
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const evt = JSON.parse(line.slice(6));
                  if (evt.type === 'download') {
                    setDlCurrent(evt.current);
                    setDlTotal(evt.total);
                    if (evt.downloaded != null) setDlDownloaded(evt.downloaded);
                  } else if (evt.type === 'download_done') {
                    setDlDownloaded(evt.downloaded);
                    setDlTotal(evt.total);
                    setDlCurrent(evt.total);
                    setPhase('parsing');
                    setParseTotal(evt.downloaded);
                    if (evt.cached) setResult(`Using ${evt.downloaded} cached filings from /data directory.`);
                  } else if (evt.type === 'parse_progress') {
                    setParseCurrent(evt.current);
                    setParseTotal(evt.total);
                    setQualified(evt.qualified);
                  } else if (evt.type === 'done') {
                    setPhase('done');
                    setResult(
                      `Done! Downloaded ${evt.downloaded} filings, parsed ${evt.parsed}, ` +
                      `${evt.qualified} qualified transactions inserted, ${evt.skipped} skipped, ${evt.errors} errors.`
                    );
                  } else if (evt.type === 'error') {
                    setResult(`Error: ${evt.message || JSON.stringify(evt)}`);
                  }
                } catch {
                  // ignore parse errors
                }
              }
            }
            return read();
          });
        return read();
      })
      .catch((err) => {
        setResult(`Error: ${err.message}`);
        setDownloading(false);
        setPhase('idle');
      });
  };

  const handleDelete = (dateStr: string) => {
    if (!window.confirm(`Delete all Form4 data for ${dateStr}?`)) return;
    form4Api.deleteByDate(dateStr)
      .then((res) => {
        setResult(`Deleted ${res.data.deleted} transactions for ${dateStr}.`);
        loadDates();
      })
      .catch((err) => setResult(`Delete failed: ${err.message}`));
  };

  return (
    <div style={{ marginTop: '0.5em' }}>
      {/* Download section */}
      <div style={{ padding: '10px 14px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', fontSize: '0.85em', marginBottom: '1em' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '1em' }}>Download Form4 from SEC EDGAR</div>
        <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.5em' }}>
          <label style={{ fontWeight: 'bold' }}>Date:</label>
          <input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)}
            style={{ ...styles.input, width: '11em' }} />
          <input type="button"
            value={downloading ? 'Processing...' : 'Download & Process'}
            onClick={handleDownload}
            disabled={downloading || !reportDate}
            style={{ ...styles.button, backgroundColor: '#b3d9ff', fontWeight: 'bold' }} />
        </div>

        {/* Progress bars */}
        {phase !== 'idle' && (
          <div style={{ marginTop: '0.5em' }}>
            <ProgressBar
              current={dlCurrent}
              total={dlTotal || dlDownloaded || 1}
              label={phase === 'downloading'
                ? `Downloading Form4 filings... (${dlDownloaded} saved)`
                : `${dlDownloaded} filings ready`}
              color={phase === 'downloading' ? '#2196f3' : '#4caf50'}
            />
            {(phase === 'parsing' || phase === 'done') && parseTotal > 0 && (
              <ProgressBar
                current={parseCurrent}
                total={parseTotal}
                label={phase === 'parsing'
                  ? `Parsing... (${qualified} inserted)`
                  : `Parsed (${qualified} transactions inserted)`}
                color={phase === 'parsing' ? '#ff9800' : '#4caf50'}
              />
            )}
          </div>
        )}
      </div>

      {/* Messages */}
      {result && (
        <div style={{
          fontSize: '0.85em', marginBottom: '0.5em', padding: '6px 12px',
          backgroundColor: result.startsWith('Done') || result.startsWith('Deleted') ? '#d4edda' : '#f8d7da',
          borderRadius: '4px', fontWeight: 'bold',
        }}>
          {result}
        </div>
      )}

      {/* Existing dates table */}
      <div style={{ fontWeight: 'bold', fontSize: '0.9em', marginBottom: '0.3em' }}>Form4 Data by Date</div>
      {loadingDates ? (
        <div style={{ textAlign: 'center', padding: '1em', color: '#999' }}>Loading...</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Total</th>
              <th style={styles.th}>Buys</th>
              <th style={styles.th}>Sells</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {dates.length === 0 ? (
              <tr><td style={{ ...styles.td, color: '#999' }} colSpan={5}>No Form4 data found.</td></tr>
            ) : (
              dates.map((d, idx) => (
                <tr key={d.date} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                  <td style={styles.td}>{d.date}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{d.total}</td>
                  <td style={{ ...styles.td, textAlign: 'right', color: 'green' }}>{d.buys}</td>
                  <td style={{ ...styles.td, textAlign: 'right', color: 'red' }}>{d.sells}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <input type="button" value="Delete" onClick={() => handleDelete(d.date)}
                      style={{ ...styles.button, fontSize: '0.85em', padding: '1px 10px', backgroundColor: '#ffcccc' }} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

/* ---- Backfill Tab ---- */
interface BackfillDateStatus {
  date: string;
  status: 'pending' | 'downloading' | 'parsing' | 'done' | 'error';
  downloaded?: number;
  total?: number;
  qualified?: number;
  error?: string;
}

const BackfillTab: React.FC = () => {
  const [running, setRunning] = useState(false);
  const [dateStatuses, setDateStatuses] = useState<BackfillDateStatus[]>([]);
  const [message, setMessage] = useState('');
  const [currentDateIdx, setCurrentDateIdx] = useState(-1);
  // Per-date progress
  const [dateDlCurrent, setDateDlCurrent] = useState(0);
  const [dateDlTotal, setDateDlTotal] = useState(0);
  const [dateDlDownloaded, setDateDlDownloaded] = useState(0);
  const [dateParseCurrent, setDateParseCurrent] = useState(0);
  const [dateParseTotal, setDateParseTotal] = useState(0);
  const [dateQualified, setDateQualified] = useState(0);
  const [datePhase, setDatePhase] = useState<'idle' | 'downloading' | 'parsing' | 'done'>('idle');
  // Range backfill
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');

  const startBackfill = (urlParams: string) => {
    setRunning(true);
    setMessage('');
    setDateStatuses([]);
    setCurrentDateIdx(-1);

    const token = localStorage.getItem('token');
    const url = `/api/form4/backfill${urlParams}`;

    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.detail || `HTTP ${response.status}`);
        }
        const reader = response.body?.getReader();
        if (!reader) throw new Error('No reader');
        const decoder = new TextDecoder();
        let buffer = '';

        const read = (): Promise<void> =>
          reader.read().then(({ done, value }) => {
            if (done) {
              setRunning(false);
              return;
            }
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const evt = JSON.parse(line.slice(6));
                  if (evt.type === 'backfill_plan') {
                    setDateStatuses(evt.dates.map((d: string) => ({ date: d, status: 'pending' })));
                  } else if (evt.type === 'date_start') {
                    setCurrentDateIdx(evt.dateIndex);
                    setDatePhase('downloading');
                    setDateDlCurrent(0); setDateDlTotal(0); setDateDlDownloaded(0);
                    setDateParseCurrent(0); setDateParseTotal(0); setDateQualified(0);
                    setDateStatuses(prev => prev.map((ds, i) =>
                      i === evt.dateIndex ? { ...ds, status: 'downloading' } : ds
                    ));
                  } else if (evt.type === 'date_download') {
                    setDateDlCurrent(evt.current);
                    setDateDlTotal(evt.total);
                    if (evt.downloaded != null) setDateDlDownloaded(evt.downloaded);
                  } else if (evt.type === 'date_download_done') {
                    setDatePhase('parsing');
                    setDateDlCurrent(evt.total); setDateDlTotal(evt.total);
                    setDateDlDownloaded(evt.downloaded);
                    setDateParseTotal(evt.downloaded);
                    setDateStatuses(prev => prev.map((ds, i) =>
                      i === evt.dateIndex ? { ...ds, status: 'parsing', downloaded: evt.downloaded, total: evt.total } : ds
                    ));
                  } else if (evt.type === 'date_parse_progress') {
                    setDateParseCurrent(evt.current);
                    setDateParseTotal(evt.total);
                    setDateQualified(evt.qualified);
                  } else if (evt.type === 'date_done') {
                    setDatePhase('done');
                    setDateQualified(evt.qualified);
                    setDateStatuses(prev => prev.map((ds, i) =>
                      i === evt.dateIndex ? { ...ds, status: 'done', downloaded: evt.downloaded, qualified: evt.qualified } : ds
                    ));
                  } else if (evt.type === 'date_error') {
                    setDateStatuses(prev => prev.map((ds, i) =>
                      i === evt.dateIndex ? { ...ds, status: 'error', error: evt.message || evt.error } : ds
                    ));
                  } else if (evt.type === 'done') {
                    setMessage(evt.message);
                    setRunning(false);
                  } else if (evt.type === 'error') {
                    setMessage(`Error: ${evt.message}`);
                    setRunning(false);
                  }
                } catch {
                  // ignore parse errors
                }
              }
            }
            return read();
          });
        return read();
      })
      .catch((err) => {
        setMessage(`Error: ${err.message}`);
        setRunning(false);
      });
  };

  const completedCount = dateStatuses.filter(d => d.status === 'done').length;
  const errorCount = dateStatuses.filter(d => d.status === 'error').length;

  return (
    <div style={{ marginTop: '0.5em' }}>
      <div style={{ padding: '10px 14px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', fontSize: '0.85em', marginBottom: '1em' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '1em' }}>Backfill Missing Form4 Data</div>
        <p style={{ margin: '0 0 8px 0', color: '#555' }}>
          Auto-backfill from the day after the latest date in the database up to yesterday.
          Weekends and US market holidays are automatically skipped.
        </p>
        <input type="button"
          value={running ? 'Backfilling...' : 'Auto Backfill'}
          onClick={() => startBackfill('')}
          disabled={running}
          style={{ ...styles.button, backgroundColor: '#b3d9ff', fontWeight: 'bold' }} />

        <div style={{ marginTop: '10px', borderTop: '1px solid #ddd', paddingTop: '8px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Backfill Date Range</div>
          <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center', flexWrap: 'wrap' }}>
            <label>From:</label>
            <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)}
              style={{ ...styles.input, width: '11em' }} />
            <label>To:</label>
            <input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)}
              style={{ ...styles.input, width: '11em' }} />
            <input type="button"
              value={running ? 'Backfilling...' : 'Backfill Range'}
              onClick={() => startBackfill(`?start_date=${rangeStart}&end_date=${rangeEnd}`)}
              disabled={running || !rangeStart || !rangeEnd}
              style={{ ...styles.button, backgroundColor: '#b3d9ff', fontWeight: 'bold' }} />
          </div>
        </div>
      </div>

      {/* Overall progress */}
      {dateStatuses.length > 0 && (
        <div style={{ marginBottom: '1em' }}>
          <ProgressBar
            current={completedCount + errorCount}
            total={dateStatuses.length}
            label={running
              ? `Overall: date ${currentDateIdx + 1} of ${dateStatuses.length} (${dateStatuses[currentDateIdx]?.date || ''})`
              : `Completed ${completedCount} of ${dateStatuses.length} dates${errorCount > 0 ? ` (${errorCount} errors)` : ''}`}
            color={running ? '#2196f3' : '#4caf50'}
          />
          {running && datePhase !== 'idle' && (
            <div style={{ marginLeft: '1em', marginTop: '4px' }}>
              {dateDlTotal > 0 && (
                <ProgressBar
                  current={dateDlCurrent}
                  total={dateDlTotal}
                  label={datePhase === 'downloading'
                    ? `Downloading filings... (${dateDlDownloaded} saved)`
                    : `Downloaded ${dateDlDownloaded} filings`}
                  color={datePhase === 'downloading' ? '#64b5f6' : '#81c784'}
                />
              )}
              {dateDlTotal === 0 && datePhase !== 'downloading' && (
                <div style={{ fontSize: '0.8em', color: '#4caf50', marginBottom: '0.3em' }}>Using cached filings</div>
              )}
              {(datePhase === 'parsing' || datePhase === 'done') && dateParseTotal > 0 && (
                <ProgressBar
                  current={dateParseCurrent}
                  total={dateParseTotal}
                  label={datePhase === 'parsing'
                    ? `Parsing... (${dateQualified} inserted)`
                    : `Parsed (${dateQualified} inserted)`}
                  color={datePhase === 'parsing' ? '#ff9800' : '#81c784'}
                />
              )}
            </div>
          )}
        </div>
      )}

      {message && (
        <div style={{
          fontSize: '0.85em', marginBottom: '0.5em', padding: '6px 12px',
          backgroundColor: message.startsWith('Error') ? '#f8d7da' : '#d4edda',
          borderRadius: '4px', fontWeight: 'bold',
        }}>
          {message}
        </div>
      )}

      {/* Date status table */}
      {dateStatuses.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Downloaded</th>
              <th style={styles.th}>Qualified</th>
            </tr>
          </thead>
          <tbody>
            {dateStatuses.map((ds, idx) => {
              const statusColor = ds.status === 'done' ? 'green'
                : ds.status === 'error' ? 'red'
                : ds.status === 'downloading' || ds.status === 'parsing' ? '#2196f3'
                : '#999';
              const statusText = ds.status === 'downloading' ? 'Downloading...'
                : ds.status === 'parsing' ? 'Parsing...'
                : ds.status === 'done' ? 'Done'
                : ds.status === 'error' ? `Error: ${ds.error || ''}`
                : 'Pending';
              return (
                <tr key={ds.date} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                  <td style={styles.td}>{ds.date}</td>
                  <td style={{ ...styles.td, color: statusColor, fontWeight: ds.status !== 'pending' ? 'bold' : 'normal' }}>{statusText}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{ds.downloaded ?? '-'}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{ds.qualified ?? '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '1em 0.5em', backgroundColor: '#fff', minHeight: 'calc(100vh - 6em)' },
  heading: { color: '#0069cc', marginTop: '1em', textAlign: 'center' },
  tabs: { display: 'flex', gap: '0', marginBottom: '1em', borderBottom: '2px solid #0d58e6' },
  tab: { padding: '6px 16px', cursor: 'pointer', fontSize: '0.85em', fontWeight: 'bold', backgroundColor: '#e6f2ff', border: '1px solid #0d58e6', borderBottom: 'none', marginRight: '2px' },
  activeTab: { backgroundColor: '#cce6ff', color: '#000' },
  searchBar: { backgroundColor: '#ececec', padding: '8px 12px', fontSize: '0.85em', marginBottom: '1em' },
  input: { padding: '2px 4px', fontFamily: 'Arial, sans-serif', fontSize: '1em', width: '10em' },
  button: { padding: '4px 16px', backgroundColor: '#ccdcfc', color: '#000', border: '1px solid #808080', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '1em' },
  table: { width: '100%', marginTop: '0.5em', border: '2px solid #0d58e6', borderCollapse: 'collapse' as const },
  th: { border: '1px solid #0d58e6', padding: '4px 6px', backgroundColor: '#cce6ff', fontWeight: 'bold', fontSize: '0.8em', textAlign: 'center' as const },
  td: { border: '1px solid #0d58e6', padding: '3px 6px', fontSize: '0.8em' },
  actionLink: { color: '#0069cc', textDecoration: 'none', cursor: 'pointer', fontSize: '0.9em' },
};

export default Form4Page;
